import React, {useEffect} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate, useLocation} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from 'antd';
import NProgress from 'nprogress';
import {getUserToken, logout} from "./util/util";
import {AuthenticatedRoutes} from "./layouts/AuthenticatedRoutes";
import {UnAuthenticatedRoutes} from "./layouts/UnAuthenticatedRoutes";
import './index.css';
import Logo from '../src/assets/img/om-full-primary-logo.svg';
import {UnAuthenticatedRoutesForgotPassword} from "./layouts/UnAuthenticatedRoutesForgotPassword";
import secureLocalStorage from "react-secure-storage";
import { queryClient } from "./lib/queryClient";
import antdTheme from './theme/antdTheme';

const App = () => {

  return (
    <ConfigProvider theme={antdTheme}>
      <QueryClientProvider client={queryClient}>
        <Router>
            <ProgressBarHandler />
          <Routes>
              {/* Root Route - Redirect based on token */}
              <Route path="/" element={<RootRedirect />} />

              {/* Protected Routes - Wrapped with PrivateRoute */}
              <Route path="/dashboard/*" element={ <PrivateRoute><AuthenticatedRoutes /></PrivateRoute> } />

              {/* Public Routes */}
              <Route path="/onboard/*" element={<UnAuthenticatedRoutes />} />
              <Route path="/forgot-password/*" element={<UnAuthenticatedRoutesForgotPassword />} />

            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
    </ConfigProvider>
  );
}

// Redirect logic for the root path "/"
const RootRedirect = () => {
    const session = getUserToken();
    const expiresAt = secureLocalStorage.getItem('expiresAt');
    const isExpired = isTokenExpired(expiresAt);

    if (session && !isExpired) {
        return <Navigate to="/dashboard" replace />;
    } else {
        logout();
        return <Navigate to="/onboard/login" replace />;
    }
};

const isTokenExpired = (expiresAt) => {
    const currentTime = Date.now();
    const expiry = parseInt(expiresAt, 10);
    return expiry && expiry < currentTime;
};

// Component to handle protected routes
const PrivateRoute = ({ children }) => {
    const token = getUserToken();
    const expiresAt = secureLocalStorage.getItem('expiresAt');
    const isExpired = isTokenExpired(expiresAt);

    useEffect(() => {
        if (!token || isExpired) {
            logout();
            window.location.href = "/onboard/login"; // Force redirect if token missing or expired
        }
    }, [token, isExpired]);

    return token && !isExpired ? children : null; // Prevent rendering protected content without token
};

// ProgressBarHandler Component to handle NProgress
const ProgressBarHandler = () => {
    const location = useLocation();

    useEffect(() => {
        NProgress.start();
        NProgress.done();
        return () => { NProgress.done(); };
    }, [location]);
    return null;
};

// 404 Not Found Page
const NotFound = () => (
    <div style={{ textAlign: "center", marginTop: "10%" }}>
        <img src={Logo} alt="OM Logo"  className='mb-5'/>
        <h1>404 - Page Not Found</h1>
        <a className="btn btn-primary mt-4" href='/'>Go to Home <i className="bi bi-house"/></a>
    </div>
);

export default App;
